import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import Accordion from '@mui/material/Accordion'
import AccordionDetails from '@mui/material/AccordionDetails'
import AccordionSummary from '@mui/material/AccordionSummary'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import IconButton from '@mui/material/IconButton'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import Paper from '@mui/material/Paper'
import Snackbar from '@mui/material/Snackbar'
import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import { useMemo, useState } from 'react'

import { OWNER_NAME, ROADMAP_VIEW as L } from '../labels'
import { shortDate } from '../lib/format'
import { api } from '../lib/api'
import type { Roadmap, RoadmapSprint, RoadmapTask } from '../lib/types'
import {
  ACCENT,
  ACCENT_SOFT,
  BAND,
  FONT_MONO,
  INK,
  INK_FAINT,
  INK_SOFT,
  LINE,
  PAPER,
  serifNumber,
} from '../theme'

interface Props {
  roadmap: Roadmap
  today: string
  ownerFilter: string
  onChanged: () => void
}

const OWNERS = ['TL', 'DS', 'SWE'] as const

/**
 * El plan como columnas de sprint con sus items debajo, y un control para moverlos.
 *
 * Se eligió un menú explícito por item en vez de arrastrar y soltar: mover una tarea aquí
 * cambia el plan del equipo, y un gesto que se dispara por accidente al hacer scroll es
 * la peor forma posible de hacerlo. El menú también funciona con teclado y en móvil, que
 * es donde se consulta este tablero la mitad del tiempo.
 */
export function RoadmapView({ roadmap, today, ownerFilter, onChanged }: Props) {
  const [menu, setMenu] = useState<{ el: HTMLElement; task: RoadmapTask } | null>(null)
  const [toast, setToast] = useState<{ msg: string; severity: 'success' | 'warning' | 'error' } | null>(
    null,
  )
  const [busy, setBusy] = useState<string | null>(null)

  const loadBySprint = useMemo(
    () => new Map(roadmap.load.map((l) => [l.sprint_id, l])),
    [roadmap.load],
  )
  const visible = (t: RoadmapTask) => !ownerFilter || t.owner === ownerFilter

  if (!roadmap.available) {
    return (
      <Paper sx={{ p: 4 }}>
        <Typography variant="h2">{L.unavailable}</Typography>
        <Typography sx={{ fontFamily: FONT_MONO, fontSize: '0.6875rem', color: INK_FAINT, mt: 1 }}>
          {roadmap.error ?? L.unavailableHint}
        </Typography>
      </Paper>
    )
  }

  async function move(task: RoadmapTask, sprintId: string | null) {
    setMenu(null)
    setBusy(task.id)
    try {
      if (sprintId === task.planned_sprint) {
        await api.resetTask(task.id)
        setToast({ msg: `${task.id} — ${L.reset}`, severity: 'success' })
      } else {
        const res = await api.moveTask(task.id, sprintId)
        setToast(
          res.warning
            ? { msg: `${L.depWarning}: ${res.warning}`, severity: 'warning' }
            : { msg: `${task.id} — ${L.moveOk}`, severity: 'success' },
        )
      }
      onChanged()
    } catch (e) {
      setToast({ msg: e instanceof Error ? e.message : L.moveFailed, severity: 'error' })
    } finally {
      setBusy(null)
    }
  }

  const unscheduled = roadmap.tasks.filter((t) => !t.sprint && visible(t))

  return (
    <>
      <Stack spacing={1.5}>
        {roadmap.sprints.map((sprint) => (
          <SprintBlock
            key={sprint.id}
            sprint={sprint}
            tasks={roadmap.tasks.filter((t) => t.sprint === sprint.id && visible(t))}
            load={loadBySprint.get(sprint.id)}
            today={today}
            busy={busy}
            onMenu={(el, task) => setMenu({ el, task })}
          />
        ))}

        <Accordion disableGutters elevation={0} sx={{ bgcolor: PAPER, border: `1px solid ${LINE}` }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Stack direction="row" spacing={1.5} alignItems="baseline" sx={{ flexWrap: 'wrap' }}>
              <Typography sx={{ fontWeight: 600 }}>{L.unscheduled}</Typography>
              <Typography sx={{ fontFamily: FONT_MONO, fontSize: '0.6875rem', color: INK_FAINT }}>
                {unscheduled.length} {L.tasks}
              </Typography>
            </Stack>
          </AccordionSummary>
          <AccordionDetails>
            <Typography sx={{ fontSize: '0.8125rem', color: INK_SOFT, mb: 1.5 }}>
              {L.unscheduledHint}
            </Typography>
            <Stack spacing={0.75}>
              {unscheduled.map((t) => (
                <TaskRow
                  key={t.id}
                  task={t}
                  busy={busy === t.id}
                  onMenu={(el) => setMenu({ el, task: t })}
                />
              ))}
            </Stack>
          </AccordionDetails>
        </Accordion>
      </Stack>

      <Menu anchorEl={menu?.el} open={Boolean(menu)} onClose={() => setMenu(null)}>
        <MenuItem disabled sx={{ opacity: 0.6, fontFamily: FONT_MONO, fontSize: '0.6875rem' }}>
          {L.moveTo}
        </MenuItem>
        {roadmap.sprints.map((s) => (
          <MenuItem
            key={s.id}
            selected={menu?.task.sprint === s.id}
            onClick={() => menu && void move(menu.task, s.id)}
          >
            <Stack direction="row" spacing={1} alignItems="baseline">
              <Box component="span" sx={{ fontFamily: FONT_MONO, fontSize: '0.75rem' }}>
                {s.id}
              </Box>
              <Box component="span" sx={{ fontSize: '0.8125rem' }}>
                {s.name}
              </Box>
              {menu?.task.planned_sprint === s.id && (
                <Box component="span" sx={{ fontSize: '0.6875rem', color: INK_FAINT }}>
                  · {L.planned}
                </Box>
              )}
            </Stack>
          </MenuItem>
        ))}
        <MenuItem
          onClick={() => menu && void move(menu.task, null)}
          sx={{ borderTop: `1px solid ${LINE}`, mt: 0.5, pt: 1 }}
        >
          {L.remove}
        </MenuItem>
      </Menu>

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={toast?.severity === 'warning' ? 10000 : 4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={toast?.severity ?? 'success'} onClose={() => setToast(null)}>
          {toast?.msg}
        </Alert>
      </Snackbar>
    </>
  )
}

function SprintBlock({
  sprint,
  tasks,
  load,
  today,
  busy,
  onMenu,
}: {
  sprint: RoadmapSprint
  tasks: RoadmapTask[]
  load?: { committed: Record<string, number>; capacity: Record<string, number> }
  today: string
  busy: string | null
  onMenu: (el: HTMLElement, t: RoadmapTask) => void
}) {
  const active = sprint.start <= today && today <= sprint.end
  return (
    <Accordion
      disableGutters
      elevation={0}
      defaultExpanded={active}
      sx={{
        bgcolor: PAPER,
        border: `1px solid ${active ? ACCENT : LINE}`,
        '&:before': { display: 'none' },
      }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Stack sx={{ width: '100%', pr: 1 }} spacing={0.75}>
          <Stack direction="row" spacing={1.5} alignItems="baseline" sx={{ flexWrap: 'wrap' }}>
            <Box component="span" sx={{ ...serifNumber, fontSize: '1.125rem', color: INK }}>
              {sprint.id}
            </Box>
            <Typography sx={{ fontWeight: 600, color: INK }}>{sprint.name}</Typography>
            <Typography sx={{ fontFamily: FONT_MONO, fontSize: '0.6875rem', color: INK_FAINT }}>
              {shortDate(sprint.start)} – {shortDate(sprint.end)}
            </Typography>
            {sprint.kind === 'refresh' && (
              <Chip size="small" label={L.refreshWindow} sx={{ height: 18, fontSize: '0.625rem' }} />
            )}
            {sprint.kind === 'holiday' && (
              <Chip size="small" label={L.holiday} sx={{ height: 18, fontSize: '0.625rem' }} />
            )}
            <Box sx={{ flex: 1 }} />
            <Typography sx={{ fontFamily: FONT_MONO, fontSize: '0.6875rem', color: INK_FAINT }}>
              {tasks.length} {L.tasks}
            </Typography>
          </Stack>
          {load && <LoadBars load={load} />}
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        {sprint.goal && (
          <Typography sx={{ fontSize: '0.8125rem', color: INK_SOFT, mb: 1.5 }}>
            {sprint.goal}
          </Typography>
        )}
        <Stack spacing={0.75}>
          {tasks.length === 0 && (
            <Typography sx={{ fontFamily: FONT_MONO, fontSize: '0.6875rem', color: INK_FAINT }}>
              {L.noTasks}
            </Typography>
          )}
          {tasks.map((t) => (
            <TaskRow key={t.id} task={t} busy={busy === t.id} onMenu={(el) => onMenu(el, t)} />
          ))}
        </Stack>
      </AccordionDetails>
    </Accordion>
  )
}

/** Comprometido contra presupuesto, por persona. Se pinta en rojo al pasarse. */
function LoadBars({
  load,
}: {
  load: { committed: Record<string, number>; capacity: Record<string, number> }
}) {
  return (
    <Stack direction="row" spacing={1.5} sx={{ flexWrap: 'wrap' }}>
      {OWNERS.map((o) => {
        const cap = load.capacity[o] ?? 0
        const used = load.committed[o] ?? 0
        if (!cap) return null
        const pct = Math.min((used / cap) * 100, 100)
        const over = used > cap
        return (
          <Stack key={o} direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 132 }}>
            <Box
              component="span"
              sx={{ fontFamily: FONT_MONO, fontSize: '0.625rem', color: INK_FAINT, width: 34 }}
            >
              {OWNER_NAME[o] ?? o}
            </Box>
            <Box sx={{ flex: 1, height: 5, bgcolor: BAND, borderRadius: 3, overflow: 'hidden' }}>
              <Box
                sx={{ width: `${pct}%`, height: '100%', bgcolor: over ? ACCENT : INK_SOFT }}
              />
            </Box>
            <Box
              component="span"
              sx={{
                fontFamily: FONT_MONO,
                fontSize: '0.625rem',
                color: over ? ACCENT : INK_FAINT,
                width: 52,
                textAlign: 'right',
              }}
            >
              {used}/{cap}
            </Box>
          </Stack>
        )
      })}
    </Stack>
  )
}

function TaskRow({
  task,
  busy,
  onMenu,
}: {
  task: RoadmapTask
  busy: boolean
  onMenu: (el: HTMLElement) => void
}) {
  return (
    <Stack
      direction="row"
      spacing={1}
      alignItems="center"
      sx={{
        px: 1.25,
        py: 0.75,
        borderRadius: 1,
        border: `1px solid ${task.moved ? ACCENT_SOFT : LINE}`,
        bgcolor: task.moved ? ACCENT_SOFT : 'transparent',
        opacity: busy ? 0.5 : 1,
      }}
    >
      <Box
        component="span"
        sx={{ fontFamily: FONT_MONO, fontSize: '0.6875rem', color: INK_SOFT, width: 62 }}
      >
        {task.id}
      </Box>
      <Typography sx={{ fontSize: '0.8125rem', color: INK, flex: 1, minWidth: 0 }} noWrap>
        {task.title}
      </Typography>
      {task.owner && (
        <Chip
          size="small"
          label={OWNER_NAME[task.owner] ?? task.owner}
          sx={{ height: 18, fontSize: '0.625rem' }}
        />
      )}
      <Box
        component="span"
        sx={{ fontFamily: FONT_MONO, fontSize: '0.625rem', color: INK_FAINT, width: 44, textAlign: 'right' }}
      >
        {task.pdays ? `${task.pdays} d` : '—'}
      </Box>
      {task.moved && (
        <Tooltip title={`${L.moved} — ${L.movedFrom(task.planned_sprint)}`}>
          <Box
            component="span"
            sx={{ fontFamily: FONT_MONO, fontSize: '0.625rem', color: ACCENT, whiteSpace: 'nowrap' }}
          >
            ✱
          </Box>
        </Tooltip>
      )}
      <Tooltip title={L.move}>
        <span>
          <IconButton size="small" disabled={busy} onClick={(e) => onMenu(e.currentTarget)}>
            <Box component="span" sx={{ fontSize: '0.875rem', lineHeight: 1, color: INK_SOFT }}>
              ⇄
            </Box>
          </IconButton>
        </span>
      </Tooltip>
    </Stack>
  )
}
